import React, { useState, useEffect, useRef, useCallback } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import { api } from '../../config';
import DmutInvitationDataTable from './component/DmutInvitationDataTable';
import StepActionBar from './component/StepActionBar';
import './component/procurementDetail.css';
import { PROCUREMENT_TYPE_LABELS } from './utils/procurementHelpers';
import { focusFieldById } from './utils/focusField';
import logger from '../../utils/logger';
import notify from '../../utils/notify';
import ActionButton from '../../Components/Common/ActionButton';
import {
  CheckField,
  FormField,
  FormSection,
  SelectField,
} from '../../Components/Form';
axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

const CARGO_CATEGORIES = [
  {
    value: 'Fuel',
    label: 'Fuel',
    name: 'isInformationAboutDMUTConfirmedFuel',
    icon: 'ri-gas-station-line',
  },
  {
    value: 'Petrochemical',
    label: 'Petrochemical',
    name: 'isInformationAboutDMUTConfirmedPetrochemical',
    icon: 'ri-flask-line',
  },
  {
    value: 'LPG',
    label: 'LPG',
    hint: 'Liquid Petroleum Gas',
    name: 'isInformationAboutDMUTConfirmedLPG',
    icon: 'ri-fire-line',
  },
];

const CONFIRMATIONS = [
  {
    name: 'isInformationAboutDMUTConfirmed',
    label:
      'By initiating the bidder invitation process, the User hereby authorizes the issuance of procurement invitations to all filtered business entities retrieved from the DMUT system.',
  },
  {
    name: 'isAcknowledgedDMUT',
    label:
      'I acknowledge that all selected entities will be formally bound by the applicable tender rules, cargo categories, and procurement auction policies',
  },
];

// What each procurement type allows; the table itself enforces the rules.
const SUPPLIER_RULES = {
  PelelanganKhusus: {
    rule: 'Every registered supplier that matches the criteria is invited automatically. The selection cannot be changed.',
    short: 'all invited',
    isValid: count => count > 0,
  },
  PemilihanLangsung: {
    rule: 'Select at least 2 registered suppliers.',
    short: 'min 2',
    isValid: count => count >= 2,
  },
  TunjukLangsung: {
    rule: 'Select exactly 1 registered supplier.',
    short: 'exactly 1',
    isValid: count => count === 1,
  },
};

const DmutInvitationTab = ({
  goNext,
  goBack,
  ids,
  form,
  setForm,
  token,
  handleSaveDraftProc,
  handleSaveCompleteDmut,
}) => {
  const [subStatus, setSubStatus] = useState({ items: [] });
  const subStatusRef = useRef(subStatus);
  subStatusRef.current = subStatus;
  const [selectedMulti2, setSelectedMulti2] = useState([]);
  const [subStatusTitles, setSubStatusTitles] = useState([]);
  const procurementId = ids;
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const dmutTableRef = useRef();
  // Everything on this tab is view-only once the procurement is completed.
  const isLocked = form.isProcurementCompleted !== false;

  const handleSelectionChange = procurementMitra => {
    setForm(prev => ({ ...prev, procurementMitra }));
  };

  const handleFormCheckBoxChange = e => {
    const { name, checked } = e.target;
    setErrors(prev => ({ ...prev, [name]: null }));
    setForm({
      ...form,
      [name]: checked ? 1 : 0,
    });
  };

  const fetchInitialCategory = useCallback(
    async procurementId => {
      try {
        const res = await axios.get(
          `${api.API_URL}/procurement/get-procurement-cargo-category?ProcurementId=${procurementId}`,
          {
            withXSRFToken: true,
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: `application/json`,
            },
          }
        );
        const items = res.items || [];
        const categories = items.map(item => item.category);
        setSelectedCategories(categories);
      } catch (err) {
        logger.error('Gagal mengambil data kategori:', err);
      }
    },
    [token]
  );

  const fetchSubStatus = useCallback(async () => {
    try {
      const response = await fetch(`${api.API_URL}/substatus`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      const items = result.data.items.map(item => ({
        value: item.id,
        label: item.name,
      }));
      setSubStatus({ items });
      return items;
    } catch (error) {
      logger.error('Error fetchSubStatus:', error);
      return [];
    }
  }, [token]);

  const fetchSubStatusGet = useCallback(
    async procurementId => {
      try {
        const response = await fetch(
          `${api.API_URL}/procurement/get-procurement-substatus?procurementId=${procurementId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const result = await response.json();

        if (result.success && result.data) {
          const masterItems =
            subStatusRef.current.items.length > 0
              ? subStatusRef.current.items
              : await fetchSubStatus();

          // One-liner validation
          const selected = (
            Array.isArray(result.data.subStatusId)
              ? result.data.subStatusId
              : []
          ).map(id => {
            const match = masterItems.find(opt => opt.value === id);
            return {
              value: id,
              label: match ? match.label : id,
            };
          });

          setSelectedMulti2(selected);
          // Sync titles so the DMUT table's SubStatus filter also works on
          // initial load, not only after a manual onChange.
          setSubStatusTitles(
            selected
              .filter(item => masterItems.some(opt => opt.label === item.label))
              .map(item => item.label)
          );
        } else {
          logger.warn('Data tidak ditemukan:', result);
          setSelectedMulti2([]);
          setSubStatusTitles([]);
        }
      } catch (error) {
        logger.error('Error fetchSubStatusGet:', error);
        setSelectedMulti2([]);
        setSubStatusTitles([]);
      }
    },
    [token, fetchSubStatus]
  );

  useEffect(() => {
    const init = async () => {
      if (!procurementId) return;
      fetchInitialCategory(procurementId);
      await fetchSubStatus(); // step 1
      await fetchSubStatusGet(procurementId); // step 2
    };
    if (procurementId) init();
  }, [fetchInitialCategory, fetchSubStatus, fetchSubStatusGet, procurementId]);

  useEffect(() => {
    if (!procurementId || selectedCategories.length === 0) return;
    const postCategory = async () => {
      const payload = {
        procurementId,
        category: selectedCategories,
      };
      try {
        axios.post(
          `${api.API_URL}/procurement/create-procurement-cargo-category`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        // eslint-disable-next-line prettier/prettier
      } catch (err) {
        logger.error('Error DMUT Invitation useEffect: ', err);
      }
    };
    postCategory();
  }, [selectedCategories, procurementId, token]);

  const handleCategoryChange = (value, checked) => {
    setSelectedCategories(prev =>
      checked ? [...prev, value] : prev.filter(item => item !== value)
    );
  };

  function handleMulti2(selectedMulti2) {
    setSelectedMulti2(selectedMulti2);
    const valuesOnly = Array.isArray(selectedMulti2)
      ? selectedMulti2.map(item => item.value)
      : [];
    const titlesOnly =
      valuesOnly.length > 0 ? selectedMulti2.map(item => item.label) : [];
    setSubStatusTitles(titlesOnly);

    const payload = {
      procurementId: procurementId, // langsung string UUID
      subStatus: valuesOnly,
    };

    axios.post(
      `${api.API_URL}/procurement/create-procurement-substatus`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  const notifyFieldErrors = msgList => {
    notify.warning(
      <div>
        Please correct the following fields:
        <ul className='mb-0 mt-1' style={{ paddingLeft: '1.25rem' }}>
          {msgList.map((msg, idx) => (
            <li key={idx}>{msg}</li>
          ))}
        </ul>
      </div>,
      { className: 'hy-toast hy-toast--warning hy-toast--wide' }
    );
  };

  // Validation mirroring validatequantityanddelivery on RequirementTab:
  // both confirmations must be checked before the "Complete DMUT
  // invitation?" confirmation is shown by handleSaveCompleteDmut.
  const validateDmutInvitation = () => {
    const tempErrors = {};
    if (!form.isInformationAboutDMUTConfirmed) {
      tempErrors.isInformationAboutDMUTConfirmed =
        'Please confirm the authorization to issue procurement invitations from the DMUT system.';
    }
    if (!form.isAcknowledgedDMUT) {
      tempErrors.isAcknowledgedDMUT =
        'Please acknowledge the tender rules, cargo categories, and procurement auction policies.';
    }

    if (Object.keys(tempErrors).length !== 0) {
      notifyFieldErrors(Object.values(tempErrors));
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSaveCompleteDmutWithValidation = async () => {
    if (!validateDmutInvitation()) {
      const firstMissing = CONFIRMATIONS.find(({ name }) => !form[name]);
      if (firstMissing) {
        window.setTimeout(
          () => focusFieldById(`consent-${firstMissing.name}`),
          60
        );
      }
      return;
    }
    const completed = await handleSaveCompleteDmut();
    if (completed) goNext?.();
  };

  const typeRule = SUPPLIER_RULES[form.procurementType];
  const selectedCount = (form.procurementMitra || []).length;
  const selectionOk = typeRule ? typeRule.isValid(selectedCount) : true;
  const isStepDone = form.isDMUTInvitationCompleted === true;
  const confirmedCount = CONFIRMATIONS.filter(({ name }) => form[name]).length;
  const allConfirmed = confirmedCount === CONFIRMATIONS.length;
  const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

  return (
    <React.Fragment>
      <div className='pd-step-head'>
        <div>
          <h5>
            <i className='ri-mail-send-line me-2'></i>
            Supplier Invitation
          </h5>
          <p>
            Set the invitation criteria, pick the registered suppliers, then
            confirm the invitation.
          </p>
        </div>
      </div>

      <div className='pd-inv-summary'>
        <div className='pd-inv-stat'>
          <span className='pd-inv-stat__icon' aria-hidden='true'>
            <i className='ri-auction-line'></i>
          </span>
          <div>
            <span className='pd-inv-stat__label'>Procurement type</span>
            <strong>
              {PROCUREMENT_TYPE_LABELS[form.procurementType] || '-'}
            </strong>
            {typeRule && <small>Suppliers: {typeRule.short}</small>}
          </div>
        </div>
        <div className='pd-inv-stat'>
          <span className='pd-inv-stat__icon' aria-hidden='true'>
            <i className='ri-filter-3-line'></i>
          </span>
          <div>
            <span className='pd-inv-stat__label'>Criteria</span>
            <strong>
              {plural(selectedMulti2.length, 'sub status', 'sub statuses')}
            </strong>
            <small>
              {plural(
                selectedCategories.length,
                'cargo category',
                'cargo categories'
              )}
            </small>
          </div>
        </div>
        <div
          className={`pd-inv-stat pd-inv-stat--${selectionOk ? 'ok' : 'warn'}`}
        >
          <span className='pd-inv-stat__icon' aria-hidden='true'>
            <i className='ri-group-line'></i>
          </span>
          <div>
            <span className='pd-inv-stat__label'>Suppliers selected</span>
            <strong>{selectedCount}</strong>
            <small>
              {selectionOk ? 'Meets the rule' : `Needs ${typeRule?.short}`}
            </small>
          </div>
        </div>
        <div
          className={`pd-inv-stat pd-inv-stat--${allConfirmed ? 'ok' : 'muted'}`}
        >
          <span className='pd-inv-stat__icon' aria-hidden='true'>
            <i className='ri-shield-check-line'></i>
          </span>
          <div>
            <span className='pd-inv-stat__label'>Confirmations</span>
            <strong>
              {confirmedCount} of {CONFIRMATIONS.length}
            </strong>
            <small>
              {allConfirmed ? 'All accepted' : 'Accept both at the bottom'}
            </small>
          </div>
        </div>
      </div>

      <FormSection
        title='Invitation criteria'
        description='Sub status and cargo category used to pick bidders from DMUT'
        icon='ri-filter-3-line'
        columns={1}
      >
        <SelectField
          label='Sub status'
          name='subStatusId'
          multi
          placeholder='Select sub status'
          options={subStatus.items}
          value={selectedMulti2.map(item => item.value)}
          onChange={(_ids, picked) => handleMulti2(picked)}
          mode={isLocked ? 'readonly' : 'editable'}
        />

        <FormField group label='Cargo category' name='cargoCategory'>
          <div className='pd-cargo-grid'>
            {CARGO_CATEGORIES.map(category => {
              const isOn = selectedCategories.includes(category.value);
              return (
                <label
                  key={category.value}
                  className={`pd-cargo${isOn ? ' is-on' : ''}${
                    isLocked ? ' is-locked' : ''
                  }`}
                >
                  <input
                    type='checkbox'
                    className='pd-cargo__input'
                    name={category.name}
                    checked={isOn}
                    disabled={isLocked}
                    onChange={e =>
                      handleCategoryChange(category.value, e.target.checked)
                    }
                  />
                  <span className='pd-cargo__icon' aria-hidden='true'>
                    <i className={category.icon}></i>
                  </span>
                  <span className='pd-cargo__text'>
                    <strong>{category.label}</strong>
                    {category.hint && <small>{category.hint}</small>}
                  </span>
                  <i
                    className={`pd-cargo__tick ${
                      isOn ? 'ri-checkbox-circle-fill' : 'ri-add-circle-line'
                    }`}
                    aria-hidden='true'
                  ></i>
                </label>
              );
            })}
          </div>
        </FormField>
      </FormSection>

      <div className='mt-3'>
        <FormSection
          title='Registered suppliers'
          description='Companies from DMUT that match the criteria above.'
          icon='ri-group-line'
          columns={1}
          actions={
            <>
              <span
                className={`pd-chip ${
                  selectionOk ? 'pd-chip--ok' : 'pd-chip--warn'
                }`}
              >
                {selectedCount} selected
                {typeRule ? ` · ${typeRule.short}` : ''}
              </span>
              {!form.isProcurementCompleted && (
                <ActionButton
                  variant='secondary'
                  size='sm'
                  icon='ri-refresh-line'
                  onClick={() => dmutTableRef.current?.refresh()}
                >
                  Sync from DMUT
                </ActionButton>
              )}
            </>
          }
        >
          {typeRule && (
            <div className='pd-inv-rule'>
              <i className='ri-information-line' aria-hidden='true'></i>
              <span>
                <strong>
                  {PROCUREMENT_TYPE_LABELS[form.procurementType]}:
                </strong>{' '}
                {typeRule.rule}
              </span>
            </div>
          )}
          <div className='pd-inv-table'>
            <DmutInvitationDataTable
              ref={dmutTableRef}
              procurementId={ids}
              token={token}
              subStatusTitles={subStatusTitles}
              cargoCategories={selectedCategories}
              onSelectionChange={handleSelectionChange}
              selectedMitraList={form.procurementMitra || []}
              procurementType={form.procurementType}
              isProcurementCompleted={form.isProcurementCompleted}
            />
          </div>
        </FormSection>
      </div>

      <div className='mt-3'>
        <FormSection
          title='Before you continue'
          description='Read and accept both confirmations to authorize the invitation.'
          icon='ri-shield-check-line'
          columns={1}
        >
          {CONFIRMATIONS.map(({ name, label }) => (
            <div className='consent-card' key={name} id={`consent-${name}`}>
              <CheckField
                switchStyle
                label={label}
                name={name}
                checked={form[name] || false}
                onChange={handleFormCheckBoxChange}
                mode={isLocked ? 'readonly' : 'editable'}
              />
              {errors[name] && (
                <div
                  className='text-danger d-flex align-items-center gap-1 mt-1'
                  style={{ fontSize: '12px' }}
                  role='alert'
                >
                  <i className='ri-error-warning-line' aria-hidden='true'></i>
                  <span>{errors[name]}</span>
                </div>
              )}
            </div>
          ))}
        </FormSection>
      </div>

      <StepActionBar
        status={
          isLocked
            ? 'Read-only after submission'
            : 'Criteria and supplier picks save automatically'
        }
        statusTone={isLocked ? 'muted' : 'ok'}
        statusIcon={isLocked ? 'ri-lock-line' : 'ri-checkbox-circle-line'}
      >
        <ActionButton
          variant='secondary'
          icon='ri-arrow-left-line'
          onClick={() => goBack?.()}
        >
          GTC
        </ActionButton>
        {!isLocked && !isStepDone && (
          <ActionButton
            variant='secondary'
            icon='ri-draft-line'
            onClick={handleSaveDraftProc}
          >
            Save as Draft
          </ActionButton>
        )}
        {isLocked || isStepDone ? (
          <ActionButton
            variant='primary'
            icon='ri-arrow-right-line'
            onClick={() => goNext?.()}
          >
            Next: Review & Submit
          </ActionButton>
        ) : (
          <ActionButton
            variant='primary'
            icon='ri-check-double-line'
            onClick={handleSaveCompleteDmutWithValidation}
          >
            Complete & Continue
          </ActionButton>
        )}
      </StepActionBar>
    </React.Fragment>
  );
};

export default DmutInvitationTab;
